import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { ChartData, ChartOptions } from "chart.js";
import { useChartConsults } from "@/app/hooks/useChartConsults.ts";
import { GroupByOption } from "@/app/services/consultService/getChartConsults.ts";

function getCssVariable(name: string) {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

const timeOptions = [
  { label: "1 minuto", value: "MINUTE", groupBy: "MINUTE" },
  { label: "5 minutos", value: "5min", groupBy: "FIVE_MINUTES" },
  { label: "15 minutos", value: "15min", groupBy: "FIFTEEN_MINUTES" },
  { label: "30 minutos", value: "30min", groupBy: "THIRTY_MINUTES" },
  { label: "1 hora", value: "1h", groupBy: "HOUR" },
  { label: "6 horas", value: "6h", groupBy: "SIX_HOURS" },
  { label: "24 horas", value: "24h", groupBy: "DAY" },
];

export function useConsultsChartController() {
  const [selected, setSelected] = useState("1h");
  const [primaryColor, setPrimaryColor] = useState<string>("");

  const selectedOption = timeOptions.find((opt) => opt.value === selected);
  const selectedLabel = selectedOption?.label ?? "";

  const {
    data: chartData,
    isLoading,
    setParams,
  } = useChartConsults({ interval: false });

  useEffect(() => {
    const primary = getCssVariable("--primary");
    if (primary) {
      setPrimaryColor(`hsl(${primary})`);
    }
  }, []);

  useEffect(() => {
    if (selectedOption?.groupBy) {
      setParams({ groupBy: selectedOption.groupBy as GroupByOption });
    }
  }, [selectedOption?.groupBy, setParams]);

  const data: ChartData<"line"> = {
    labels:
      chartData?.consults.map(({ date }) =>
        format(new Date(date), "dd/MM HH:mm", { locale: ptBR }),
      ) ?? [],
    datasets: [
      {
        fill: true,
        data: chartData?.consults.map((item) => item.count) ?? [],
        borderColor: primaryColor,
        backgroundColor: primaryColor.replace(")", " / 0.2)"),
        pointRadius: 2,
        pointHitRadius: 40,
        pointBackgroundColor: primaryColor,
        tension: 0.4,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => `Consultas: ${context.raw}`,
        },
        displayColors: false,
      },
    },
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: {
            size: 14,
            weight: 600,
          },
        },
      },
      y: {
        grid: { color: "#e5e7eb" },
        ticks: {
          callback: function (value: number | string) {
            const v = typeof value === "string" ? parseFloat(value) : value;
            return v >= 1000 ? `${(v / 1000).toFixed(0)}K` : `${v}`;
          },
          font: { size: 14, weight: 600 },
          maxTicksLimit: 4,
        },
      },
    },
  };

  const handleSelect = (value: string) => {
    setSelected(value);
  };

  return {
    selected,
    selectedLabel,
    timeOptions,
    handleSelect,
    data,
    options,
    isLoading,
  };
}
